import { PRECISION_FACTOR } from "@silo/types";
import {
  DebtAssetWithValues,
  DepositAssetWithValues,
  NormalizedBaseLtv,
  NormalizedCalculateLtvParams,
  NormalizedDebtLtvAsset,
  NormalizedDebtAssetMetrics,
  NormalizedDepositLtvAsset,
  NormalizedDepositAssetMetrics,
} from "./types";
import { Array, BigInt, pipe, Struct } from "effect";

export function performCalculations(
  params: NormalizedCalculateLtvParams
): NormalizedBaseLtv {
  const depositAssetsWithValues = params.depositAssets.map(
    mapToDepositAssetWithValues
  );

  const debtAssetsWithValues = params.debtAssets.map(mapToDebtAssetWithValues);

  const availableToBorrowValue = pipe(
    depositAssetsWithValues,
    Array.map(Struct.get("availableToBorrowValue")),
    BigInt.sumAll
  );

  const liquidationThresholdValue = pipe(
    depositAssetsWithValues,
    Array.map(Struct.get("liquidationThresholdValue")),
    BigInt.sumAll
  );

  const depositValue = pipe(
    depositAssetsWithValues,
    Array.map(Struct.get("depositValue")),
    BigInt.sumAll
  );

  const borrowedValue = pipe(
    debtAssetsWithValues,
    Array.map(Struct.get("borrowedValue")),
    BigInt.sumAll
  );

  const remainingToBorrowValue = availableToBorrowValue - borrowedValue;
  const liquidationBufferValue = liquidationThresholdValue - borrowedValue;

  const depositAssets = depositAssetsWithValues.map((asset) =>
    mapToDepositAssetMetrics(
      asset,
      remainingToBorrowValue,
      liquidationBufferValue
    )
  );

  const debtAssets = debtAssetsWithValues.map((asset) =>
    mapToDebtAssetMetrics(asset, remainingToBorrowValue, liquidationBufferValue)
  );

  if (depositValue === 0n) {
    return getNoDepositLtv(borrowedValue, depositAssets, debtAssets);
  }

  const userLtv = (borrowedValue * PRECISION_FACTOR) / depositValue;
  const userMaxLtv = (availableToBorrowValue * PRECISION_FACTOR) / depositValue;
  const userLiquidationThreshold =
    (liquidationThresholdValue * PRECISION_FACTOR) / depositValue;
  const riskFactor =
    (borrowedValue * PRECISION_FACTOR) / liquidationThresholdValue;
  const healthFactor = PRECISION_FACTOR - riskFactor;
  const borrowedPct =
    (borrowedValue * PRECISION_FACTOR) / availableToBorrowValue;
  const remainingToBorrowPct =
    (remainingToBorrowValue * PRECISION_FACTOR) / availableToBorrowValue;

  const baseLtv: NormalizedBaseLtv = {
    userLtv,
    userMaxLtv,
    userLiquidationThreshold,
    healthFactor,
    riskFactor,
    depositValue,
    liquidationThresholdValue,
    availableToBorrowValue,
    borrowedValue,
    remainingToBorrowValue,
    liquidationBufferValue,
    borrowedPct,
    remainingToBorrowPct,
    depositAssets,
    debtAssets,
  };

  return baseLtv;
}

export function mapToDepositAssetWithValues(
  asset: NormalizedDepositLtvAsset
): DepositAssetWithValues {
  const { balance, priceNative, maxLtv, lt } = asset;
  const depositValue = (balance * priceNative) / PRECISION_FACTOR;

  return {
    ...asset,
    depositValue,
    availableToBorrowValue: (maxLtv * depositValue) / PRECISION_FACTOR,
    liquidationThresholdValue: (lt * depositValue) / PRECISION_FACTOR,
  };
}

export function mapToDebtAssetWithValues(
  asset: NormalizedDebtLtvAsset
): DebtAssetWithValues {
  const { balance, priceNative } = asset;

  return {
    ...asset,
    borrowedValue: (balance * priceNative) / PRECISION_FACTOR,
  };
}

export function mapToDepositAssetMetrics(
  asset: DepositAssetWithValues,
  remainingToBorrowValue: bigint,
  liquidationBufferValue: bigint
): NormalizedDepositAssetMetrics {
  const { type, borrowValueToReduce } =
    asset.availableToBorrowValue < remainingToBorrowValue
      ? ({
          type: "protocolBalance",
          borrowValueToReduce: asset.availableToBorrowValue,
        } as const)
      : ({
          type: "ltvLimit",
          borrowValueToReduce: remainingToBorrowValue,
        } as const);

  const maxWithdrawValue =
    (borrowValueToReduce * PRECISION_FACTOR) / asset.maxLtv;
  const maxWithdrawTokens =
    (maxWithdrawValue * PRECISION_FACTOR) / asset.priceNative;
  const { tokenAddress, decimals } = asset;

  const getLiquidationPrice = (): bigint | null => {
    if (asset.liquidationThresholdValue <= liquidationBufferValue) {
      return null;
    }

    if (asset.balance === 0n) {
      return null;
    }

    const minLiquidationThresholdValue =
      asset.liquidationThresholdValue - liquidationBufferValue;
    const minCollateralValue =
      (minLiquidationThresholdValue * PRECISION_FACTOR) / asset.lt;
    const liquidationPrice =
      (minCollateralValue * PRECISION_FACTOR) / asset.balance;

    return liquidationPrice;
  };

  return {
    tokenAddress,
    decimals,
    maxWithdrawTokens: { type, value: maxWithdrawTokens },
    liquidationPriceNative: getLiquidationPrice(),
  };
}

export function mapToDebtAssetMetrics(
  asset: DebtAssetWithValues,
  remainingToBorrowValue: bigint,
  liquidationBufferValue: bigint
): NormalizedDebtAssetMetrics {
  const borrowedTokens = asset.balance;
  const remainingToBorrowTokens =
    (remainingToBorrowValue * PRECISION_FACTOR) / asset.priceNative;
  const availableToBorrowTokens = borrowedTokens + remainingToBorrowTokens;
  const { tokenAddress, decimals } = asset;

  const getLiquidationPrice = (): bigint | null => {
    if (asset.balance === 0n) {
      return null;
    }

    const maxValue = liquidationBufferValue + asset.borrowedValue;
    const liquidationPrice = (maxValue * PRECISION_FACTOR) / asset.balance;

    return liquidationPrice;
  };

  return {
    tokenAddress,
    decimals,
    borrowedTokens,
    remainingToBorrowTokens,
    availableToBorrowTokens,
    liquidationPriceNative: getLiquidationPrice(),
  };
}

function getNoDepositLtv(
  borrowedValue: bigint,
  depositAssets: NormalizedDepositAssetMetrics[],
  debtAssets: NormalizedDebtAssetMetrics[]
): NormalizedBaseLtv {
  return {
    userLtv: 0n,
    userMaxLtv: 0n,
    userLiquidationThreshold: 0n,
    healthFactor: 0n,
    riskFactor: PRECISION_FACTOR,
    depositValue: 0n,
    liquidationThresholdValue: 0n,
    availableToBorrowValue: 0n,
    borrowedValue,
    remainingToBorrowValue: 0n,
    liquidationBufferValue: 0n,
    borrowedPct: 0n,
    remainingToBorrowPct: 0n,
    depositAssets,
    debtAssets,
  };
}
